import { IconButton, TextField, Typography } from '@mui/material';
import { PaperPlaneRight, X } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { Edge, Node, useReactFlow } from 'reactflow';
import { io, Socket } from 'socket.io-client';
import { ChatStreamEvent } from '../../data/responses/chatbot';
import { useAuthStore } from '../../store/storeAuth';
import useFlowsStore from '../../store/storeFlows';
import { axiosInstance } from '../../service/ApiService';
import { FlowService } from '../../service/FlowService';
import { generateRandomId } from '../../util/generateId';
import {
  MiniChatContainer,
  MiniChatHeader,
  MiniChatInputArea,
  MiniChatMessages,
  MiniMessageBubble,
  MiniMessageContainer,
  MiniTypingIndicator,
  OutputPreviewCard,
  InsertButton,
} from '../PromptAssistant/StyledComponents';
import {
  NODE_ASSISTANT_FLOW_ID,
  NODE_ASSISTANT_INPUT_ID,
  NodeAssistantMessage,
  PendingNodeOutput,
} from './types';

interface NodeAssistantChatProps {
  nodeId: string;
  nodeType: string;
  nodeData: Record<string, unknown>;
  edges: Edge[];
  flowSummary: string;
  sessionId?: string;
  onClose: () => void;
}

const CONTEXT_CHAR_LIMIT = 120000;
const TRUNCATION_MARKER = '...[truncated]';

const extractOutput = (text: string): string | null => {
  const match = text.match(/<output>([\s\S]*?)<\/output>/);
  return match ? match[1].trim() : null;
};

const extractOutputNode = (text: string): Node | null => {
  const match = text.match(/<output-node>([\s\S]*?)<\/output-node>/);
  if (!match) return null;
  try {
    return JSON.parse(match[1].trim()) as Node;
  } catch {
    return null;
  }
};

const extractOutputEdges = (text: string): Edge[] | null => {
  const match = text.match(/<output-edges>([\s\S]*?)<\/output-edges>/);
  if (!match) return null;
  try {
    const content = match[1].trim();
    const json = content.startsWith('[') ? content : `[${content}]`;
    return JSON.parse(json) as Edge[];
  } catch {
    return null;
  }
};

const stripNodeTags = (text: string): string =>
  text
    .replace(/<output>[\s\S]*?<\/output>/g, '')
    .replace(/<output-node>[\s\S]*?<\/output-node>/g, '')
    .replace(/<output-edges>[\s\S]*?<\/output-edges>/g, '')
    .trim();

const findLargestStringPath = (obj: unknown, path: string[] = []): { path: string[]; length: number } | null => {
  if (typeof obj === 'string') return { path, length: obj.length };
  if (typeof obj === 'object' && obj !== null) {
    const entries: [string, unknown][] = Array.isArray(obj)
      ? obj.map((v, i) => [String(i), v])
      : Object.entries(obj as Record<string, unknown>);
    let largest: { path: string[]; length: number } | null = null;
    for (const [key, value] of entries) {
      const result = findLargestStringPath(value, [...path, key]);
      if (result && (!largest || result.length > largest.length)) largest = result;
    }
    return largest;
  }
  return null;
};

const getAtPath = (obj: Record<string, unknown>, path: string[]): string =>
  path.reduce((cur, key) => (cur as Record<string, unknown>)[key], obj as unknown) as string;

const setAtPath = (obj: Record<string, unknown>, path: string[], value: string): void => {
  const last = path[path.length - 1];
  const parent = path.slice(0, -1).reduce((cur, key) => (cur as Record<string, unknown>)[key], obj as unknown) as Record<string, unknown>;
  parent[last] = value;
};

const buildMessageText = (contextData: Record<string, unknown>, userMessage: string): string =>
  `[Context]: ${JSON.stringify(contextData)}\n\n[User Message]: ${userMessage}`;

const truncateContextToLimit = (contextData: Record<string, unknown>, userMessage: string): string => {
  const mutable: Record<string, unknown> = JSON.parse(JSON.stringify(contextData));
  let text = buildMessageText(mutable, userMessage);
  while (text.length > CONTEXT_CHAR_LIMIT) {
    const largest = findLargestStringPath(mutable);
    if (!largest || largest.length <= TRUNCATION_MARKER.length) break;
    const excess = text.length - CONTEXT_CHAR_LIMIT;
    const current = getAtPath(mutable, largest.path);
    const newLength = Math.max(0, current.length - excess - TRUNCATION_MARKER.length);
    setAtPath(mutable, largest.path, current.slice(0, newLength) + TRUNCATION_MARKER);
    text = buildMessageText(mutable, userMessage);
  }
  return text;
};

const NodeAssistantChat = ({
  nodeId,
  nodeType,
  nodeData,
  edges,
  flowSummary,
  sessionId,
  onClose,
}: NodeAssistantChatProps) => {
  const { user } = useAuthStore(state => state);
  const { flowId, setSaving } = useFlowsStore(state => state);
  const { setNodes, setEdges, getNode, getEdges } = useReactFlow();
  const isDark = user?.theme === 'dark';

  const [messages, setMessages] = useState<NodeAssistantMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [socketClient, setSocketClient] = useState<Socket | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [pendingOutput, setPendingOutput] = useState<PendingNodeOutput | null>(null);

  const sessionIdRef = useRef<string>(sessionId ?? generateRandomId());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const streamingContentRef = useRef('');
  const hasSentFirstMessageRef = useRef(false);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const handleOutputDetected = (fullText: string) => {
    const previewText = extractOutput(fullText);
    const outputNode = extractOutputNode(fullText);
    const outputEdges = extractOutputEdges(fullText);

    if (previewText !== null || outputNode !== null || outputEdges !== null) {
      const cleanMessage = stripNodeTags(fullText);
      const currentNode = getNode(nodeId);
      const resolvedNode = outputNode ?? (currentNode as Node);
      const resolvedEdges = outputEdges;

      setMessages(prev => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;
        if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
          if (!cleanMessage) return updated.slice(0, lastIdx);
          updated[lastIdx] = { ...updated[lastIdx], content: cleanMessage };
        }
        return updated;
      });

      setPendingOutput({
        previewText: previewText ?? '',
        node: resolvedNode,
        edges: resolvedEdges,
        rawResponse: fullText,
      });
    }
  };

  const handleInsert = async () => {
    if (!pendingOutput || !flowId) return;

    const dataToSave = { ...(pendingOutput.node.data as Record<string, unknown>), isEmptyState: false };
    const updatedData = { ...dataToSave, _remountKey: Date.now() };
    setNodes(prev => prev.map(n => n.id === nodeId ? { ...n, data: updatedData } : n));

    setSaving({ finished: false, lastSaved: new Date().toISOString(), status: 'loading', flowId });

    try {
      await FlowService.updateNodeData(flowId, {
        id: nodeId,
        data: dataToSave,
        changeType: 'default',
      });
    } catch {
    }

    if (pendingOutput.edges !== null) {
      const allEdges = getEdges();
      const unrelatedEdges = allEdges.filter(e => e.source !== nodeId && e.target !== nodeId);
      const updatedEdges = [...unrelatedEdges, ...pendingOutput.edges];

      setEdges(updatedEdges);

      try {
        await FlowService.updateFlow(flowId, { edges: updatedEdges });
      } catch {
      }
    }

    try {
      setSaving({ finished: true, lastSaved: new Date().toISOString(), status: 'saved', flowId });
    } catch {
    }

    onCloseRef.current();
  };

  useEffect(() => {
    const productionUrl = import.meta.env.VITE_API_URL_PRODUCTION as string;
    const socket = io(productionUrl);

    socket.on('connect', () => {
      setSocketClient(socket);
    });

    socket.on('chatbot-req', (socketResponse: ChatStreamEvent) => {
      if (socketResponse.data.type === 'stream') {
        const socketData = socketResponse.data;

        switch (socketData.event) {
          case 'stream_start':
            streamingContentRef.current = '';
            setPendingOutput(null);
            setMessages(prev => [
              ...prev,
              { role: 'assistant', content: '', timestamp: socketData.data.timestamp },
            ]);
            break;

          case 'stream_message':
            streamingContentRef.current += socketData.data.text;
            setMessages(prev => {
              const updated = [...prev];
              const lastIdx = updated.length - 1;
              if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
                updated[lastIdx] = {
                  ...updated[lastIdx],
                  content: updated[lastIdx].content + socketData.data.text,
                };
              }
              return updated;
            });
            break;

          case 'stream_end':
            setIsTyping(false);
            handleOutputDetected(streamingContentRef.current);
            break;

          case 'stream_error':
            setIsTyping(false);
            break;

          default:
            break;
        }
      } else {
        const socketData = socketResponse.data;

        switch (socketData.event) {
          case 'message': {
            const text = socketData.data?.[0]?.text ?? '';
            const timestamp = socketData.data?.[0]?.timestamp ?? new Date().toISOString();
            setMessages(prev => [...prev, { role: 'assistant', content: text, timestamp }]);
            setIsTyping(false);
            handleOutputDetected(text);
            break;
          }

          case 'error':
            setIsTyping(false);
            break;

          default:
            break;
        }
      }
    });

    return () => {
      socket.off('connect');
      socket.off('chatbot-req');
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping, pendingOutput]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || !socketClient?.id) return;

    if (!expanded) setExpanded(true);

    setPendingOutput(null);

    const userMessage: NodeAssistantMessage = {
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);
    setInputValue('');

    const isFirstMessage = !hasSentFirstMessageRef.current;
    hasSentFirstMessageRef.current = true;

    let messageText = userMessage.content;

    if (isFirstMessage) {
      const currentNode = getNode(nodeId);
      const connectedEdges = edges.filter(e => e.source === nodeId || e.target === nodeId);
      const contextData: Record<string, unknown> = {
        flowSummary,
        flowId: flowId || '',
        sessionId: sessionIdRef.current,
        nodeJson: currentNode ?? { id: nodeId, type: nodeType, data: nodeData },
        connectedEdges,
      };
      messageText = truncateContextToLimit(contextData, userMessage.content);
    }

    const variableInputs: Array<{ inputText?: string; inputId?: string }> = [
      { inputId: NODE_ASSISTANT_INPUT_ID, inputText: messageText },
    ];

    try {
      const productionUrl = import.meta.env.VITE_API_URL_PRODUCTION as string;
      const sessionId = sessionIdRef.current;
      await axiosInstance.post(
        `${productionUrl}/flux/chatbot-api-socket?flowId=${NODE_ASSISTANT_FLOW_ID}&sessionId=${sessionId}&socketId=${socketClient.id}`,
        { sessionId, variableInputs },
      );
    } catch {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <MiniChatContainer isDark={isDark} expanded={expanded}>
      <MiniChatHeader isDark={isDark}>
        <Typography
          sx={{ fontSize: '14px', fontWeight: 600, color: isDark ? '#F2F4F7' : '#101828' }}
        >
          Node Assistant
        </Typography>
        <IconButton onClick={onClose} sx={{ p: '4px' }}>
          <X size={16} color={isDark ? '#98A2B3' : '#475467'} />
        </IconButton>
      </MiniChatHeader>

      {expanded ? (
        <MiniChatMessages>
          {messages.map((msg, index) => (
            <MiniMessageContainer key={index} isUser={msg.role === 'user'}>
              <MiniMessageBubble isUser={msg.role === 'user'} isDark={isDark}>
                {msg.content}
              </MiniMessageBubble>
            </MiniMessageContainer>
          ))}

          {isTyping && messages[messages.length - 1]?.role !== 'assistant' && (
            <MiniTypingIndicator isDark={isDark}>
              <span className="dot" />
              <span className="dot" />
              <span className="dot" />
            </MiniTypingIndicator>
          )}

          <div ref={messagesEndRef} />
        </MiniChatMessages>
      ) : (
        <Typography
          sx={{ padding: '16px 14px 8px', fontSize: '13px', color: isDark ? '#98A2B3' : '#667085' }}
        >
          What can I help you with?
        </Typography>
      )}

      {pendingOutput && (
        <>
          <OutputPreviewCard isDark={isDark}>
            {pendingOutput.previewText}
          </OutputPreviewCard>
          <InsertButton variant="contained" onClick={handleInsert} fullWidth={false}>
            Apply
          </InsertButton>
        </>
      )}

      <MiniChatInputArea isDark={isDark}>
        <TextField
          fullWidth
          size="small"
          placeholder="Describe what you need..."
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={!socketClient?.connected}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '13px',
              borderRadius: '8px',
              backgroundColor: isDark ? '#101828' : '#F9FAFB',
              color: isDark ? '#D0D5DD' : '#101828',
              '& fieldset': { borderColor: isDark ? '#344054' : '#D0D5DD' },
              '&:hover fieldset': { borderColor: isDark ? '#475467' : '#98A2B3' },
              '&.Mui-focused fieldset': { borderColor: '#3650DE' },
            },
          }}
        />
        <IconButton
          onClick={handleSendMessage}
          disabled={!inputValue.trim() || !socketClient?.connected}
          sx={{
            p: '6px',
            backgroundColor: '#3650DE',
            borderRadius: '8px',
            '&:hover': { backgroundColor: '#2A40B0' },
            '&.Mui-disabled': { backgroundColor: isDark ? '#344054' : '#D0D5DD' },
          }}
        >
          <PaperPlaneRight size={16} color="#FFFFFF" weight="fill" />
        </IconButton>
      </MiniChatInputArea>
    </MiniChatContainer>
  );
};

export default NodeAssistantChat;
